"use client";

import * as React from "react";
import { Check } from "lucide-react";

import { cn } from "../../lib/utils";

/**
 * Job D — the pick-one section inside a panel (`docs/picker-taxonomy.md` §1.5,
 * §3.4, §4.4).
 *
 * A `Popover` panel is a container, not a list: a calendar grid, a colour
 * grid, a preset column. When such a panel contains "a set of buttons where
 * one is current", that set is a **radiogroup** — `role="radiogroup"` /
 * `role="radio"` / `aria-checked`, roving tabindex, arrow keys that select as
 * they move. It is not a listbox (that is Job A/B) and not a menu (Job C).
 *
 * Two consumers today — the 20-swatch board colour grid and the 6-item
 * date-range preset column — both of which were bare `<button>` lists whose
 * only selection signal was a background wash or a ring, i.e. nothing at all
 * to a screen reader.
 *
 * Accessible names are **words, never data**: a swatch is `label="Indigo"`,
 * never `label="#4763c4"`. The hex is the value; the name is the label.
 */

interface ChoiceGroupContextValue {
  value?: string;
  select: (value: string) => void;
  orientation: "vertical" | "grid";
}

const ChoiceGroupContext = React.createContext<ChoiceGroupContextValue | null>(
  null,
);

export interface ChoiceGroupProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onChange" | "children"
> {
  value?: string;
  onValueChange?: (value: string) => void;
  /** Accessible name for the radiogroup. Required — a radiogroup with no name is unusable. */
  label: string;
  orientation?: "vertical" | "grid";
  /** Grid only. The row stride for `↑`/`↓`. */
  columns?: number;
  className?: string;
  /** `ChoiceItem`s. */
  children: React.ReactNode;
}

const ChoiceGroup = React.forwardRef<HTMLDivElement, ChoiceGroupProps>(
  function ChoiceGroup(
    {
      value,
      onValueChange,
      label,
      orientation = "vertical",
      columns = 5,
      className,
      children,
      style,
      ...props
    },
    forwardedRef,
  ) {
    const innerRef = React.useRef<HTMLDivElement | null>(null);

    const setRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    const items = React.useCallback(
      () =>
        Array.from(
          innerRef.current?.querySelectorAll<HTMLButtonElement>(
            '[role="radio"]:not([disabled])',
          ) ?? [],
        ),
      [],
    );

    /**
     * A radiogroup must always contain exactly one tab stop. Every item sets
     * `tabIndex={-1}` unless it is the checked one, so a group with no value
     * yet — a fresh colour picker, an unset preset column — would otherwise
     * be unreachable by keyboard entirely. Reading the DOM is what makes this
     * work regardless of how the caller nested or mapped its children.
     */
    React.useEffect(() => {
      const radios = items();
      if (radios.length === 0) return;
      if (radios.some((radio) => radio.tabIndex === 0)) return;
      radios[0].tabIndex = 0;
    });

    const focusIndex = React.useCallback(
      (index: number) => {
        const radios = items();
        if (radios.length === 0) return;
        const wrapped =
          ((index % radios.length) + radios.length) % radios.length;
        const target = radios[wrapped];
        target.focus();
        // Standard radiogroup behaviour: arrow keys select as they move.
        const next = target.getAttribute("data-value");
        if (next !== null) onValueChange?.(next);
      },
      [items, onValueChange],
    );

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        const radios = items();
        const index = radios.indexOf(
          document.activeElement as HTMLButtonElement,
        );
        if (index === -1) return;

        const rowStride =
          orientation === "grid" ? Math.max(1, Math.trunc(columns)) : 1;

        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            focusIndex(index + rowStride);
            break;
          case "ArrowUp":
            event.preventDefault();
            focusIndex(index - rowStride);
            break;
          case "ArrowRight":
            event.preventDefault();
            focusIndex(index + 1);
            break;
          case "ArrowLeft":
            event.preventDefault();
            focusIndex(index - 1);
            break;
          case "Home":
            event.preventDefault();
            focusIndex(0);
            break;
          case "End":
            event.preventDefault();
            focusIndex(radios.length - 1);
            break;
          default:
            break;
        }
      },
      [columns, focusIndex, items, orientation],
    );

    const context = React.useMemo<ChoiceGroupContextValue>(
      () => ({
        value,
        select: (next: string) => onValueChange?.(next),
        orientation,
      }),
      [onValueChange, orientation, value],
    );

    return (
      <ChoiceGroupContext.Provider value={context}>
        <div
          ref={setRef}
          role="radiogroup"
          aria-label={label}
          data-slot="choice-group"
          data-orientation={orientation}
          onKeyDown={handleKeyDown}
          className={cn(
            orientation === "grid" ? "grid gap-1.5" : "flex flex-col",
            className,
          )}
          style={
            orientation === "grid"
              ? {
                  gridTemplateColumns: `repeat(${Math.max(
                    1,
                    Math.trunc(columns),
                  )}, minmax(0, 1fr))`,
                  ...style,
                }
              : style
          }
          {...props}
        >
          {children}
        </div>
      </ChoiceGroupContext.Provider>
    );
  },
);

export interface ChoiceItemProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value"
> {
  value: string;
  /** Accessible name — a word, never a raw hex string. */
  label: string;
}

/** Vertical list row: a preset, a named option inside an editor panel. */
const CHOICE_ITEM_VERTICAL = [
  "flex h-control w-full items-center gap-2 rounded-sm px-2 text-left text-sm text-text",
  "transition-[background-color,color] duration-100 ease-out-fast",
  "hover:bg-hover",
  "data-[state=checked]:bg-selected data-[state=checked]:font-medium",
  "focus-visible:outline-none focus-fill",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");

/**
 * Grid cell: a swatch. Checked is marked by the glyph the button renders over
 * its own fill — the outline that used to carry it is gone with every other
 * ring, and a swatch has no room for a tone change it would not swallow.
 */
const CHOICE_ITEM_GRID = [
  "relative grid size-6 place-items-center rounded-sm",
  "transition-[transform] duration-100 ease-out-fast",
  "data-[state=checked]:scale-110",
  "focus-visible:outline-none focus-fill",
  "disabled:cursor-not-allowed disabled:opacity-50",
].join(" ");

/** Black or white by the fill's relative luminance, so the tick reads on any swatch. */
function tickToneClass(hex?: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? "");
  if (!m) return "text-white";
  const [r, g, b] = [0, 2, 4].map(
    (i) => parseInt(m[1].slice(i, i + 2), 16) / 255,
  );
  const lin = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.5 ? "text-black" : "text-white";
}

const ChoiceItem = React.forwardRef<HTMLButtonElement, ChoiceItemProps>(
  function ChoiceItem(
    { value, label, className, children, onClick, ...props },
    ref,
  ) {
    const context = React.useContext(ChoiceGroupContext);
    const checked = context?.value === value;

    return (
      <button
        ref={ref}
        type="button"
        role="radio"
        aria-checked={checked}
        aria-label={label}
        data-slot="choice-item"
        data-value={value}
        data-state={checked ? "checked" : "unchecked"}
        // Roving tabindex: the checked item is the group's single tab stop.
        // `ChoiceGroup` promotes the first item when nothing is checked yet.
        tabIndex={checked ? 0 : -1}
        onClick={(event) => {
          context?.select(value);
          onClick?.(event);
        }}
        className={cn(
          context?.orientation === "grid"
            ? CHOICE_ITEM_GRID
            : CHOICE_ITEM_VERTICAL,
          className,
        )}
        {...props}
      >
        {children}
        {context?.orientation === "grid" && checked ? (
          <Check
            aria-hidden="true"
            strokeWidth={3}
            className={cn(
              "size-3.5",
              tickToneClass(
                (props.style as React.CSSProperties | undefined)
                  ?.backgroundColor as string | undefined,
              ),
            )}
          />
        ) : null}
      </button>
    );
  },
);

export { ChoiceGroup, ChoiceItem };
